import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/ui/components/Button';
import { useAuthStore } from '@/store/authStore';
import { useNotificationStore } from '@/store/notificationStore';
import { WalletSigningModal } from '@/components/transactions/WalletSigningModal';
import { toSigningPayload } from '@/lib/wallet';
import type { SigningPayload, XamanPayloadResponse } from '@/types/signing';
import { AuctionCountdown } from './AuctionCountdown';
import { PlaceBidModal } from './PlaceBidModal';
import { apiClient } from '@/api/client';
import { formatAddress } from '@/utils/formatters';
import {
  cancelAuction,
  confirmAuctionCancellation,
  type Auction,
  type AuctionBid,
  type CancelAuctionResponse,
} from '@/api/auctions';

const DROPS_PER_XRP = 1_000_000;

interface Props {
  auction: Auction;
  bids: AuctionBid[];
  onChange?: () => void;
}

function formatXrp(drops: string | null | undefined): string {
  if (!drops) return '—';
  return (Number(drops) / DROPS_PER_XRP).toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function formatEndsAt(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Renders the auction state for an NFT — countdown, "Cancel auction" (owner)
 * or "Make Offer" (everyone else), bid history.
 *
 * The page is responsible for fetching the auction (via useActiveAuction) and
 * gating the standard offers UI; this component just renders. Pass `onChange`
 * to refresh after a cancel/bid succeeds.
 */
export const ActiveAuctionView: React.FC<Props> = ({ auction, bids, onChange }) => {
  const [bidOpen, setBidOpen] = useState(false);
  const [signingCancel, setSigningCancel] = useState<SigningPayload | null>(null);
  const [auctionIdForCancel, setAuctionIdForCancel] = useState<string | null>(null);
  const [signingBidCancel, setSigningBidCancel] = useState<SigningPayload | null>(null);
  const [cancellingBidId, setCancellingBidId] = useState<string | null>(null);

  const { user } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const refresh = () => onChange?.();

  const isOwner = !!user && user.xrpl_address === auction.seller_address;
  const isWinner = !!user && user.xrpl_address === auction.winning_bidder_address;
  const highestBid = bids.length > 0 ? bids[0] : null;
  const isActive = auction.status === 'active';
  const ended = !isActive || new Date(auction.expires_at).getTime() <= Date.now();
  const headline =
    auction.status === 'settled' ? 'Auction sold'
    : auction.status === 'expired_no_reserve' ? 'Auction ended without meeting reserve'
    : auction.status === 'settlement_stuck' ? 'Settlement in progress'
    : auction.status === 'cancelled' ? 'Auction cancelled'
    : ended ? 'Auction ended'
    : 'Auction live';

  async function handleCancel() {
    const res = (await cancelAuction(auction.id)) as CancelAuctionResponse;
    if (!res.success || !res.data) {
      showError(res.error || 'Failed to cancel auction');
      return;
    }
    if (!res.data.signingIntent && !res.data.payload) {
      // Auction was cancelled outright (no on-chain offer to revoke).
      showSuccess('Auction cancelled');
      void refresh();
      return;
    }
    setAuctionIdForCancel(auction.id);
    setSigningCancel(toSigningPayload(res.data.signingIntent ?? res.data.payload));
  }

  async function handleCancelSigned(_txHash: string) {
    if (!auctionIdForCancel) return;
    const res = await confirmAuctionCancellation(auctionIdForCancel);
    if (res.success) {
      showSuccess('Auction cancelled');
      void refresh();
    } else {
      showError((res as any).error || 'Cancellation submitted but confirmation failed');
    }
    setSigningCancel(null);
    setAuctionIdForCancel(null);
  }

  async function handleCancelOwnBid(offerId: string) {
    if (!user?.xrpl_address) {
      showError('You must be logged in to cancel a bid');
      return;
    }
    try {
      const response = await apiClient.post<{
        success: boolean;
        error?: string;
        data?: {
          signingIntent?: { intentId: string; provider: string; txJson?: Record<string, unknown> };
          payload?: XamanPayloadResponse;
        };
      }>('/offers/cancel', {
        nftTokenId: auction.nft_token_id,
        offerIndex: offerId,
        userAddress: user.xrpl_address,
      });
      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setCancellingBidId(offerId);
        setSigningBidCancel(toSigningPayload(response.data.signingIntent ?? response.data.payload));
      } else {
        showError(response.error || 'Failed to cancel bid');
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to cancel bid');
    }
  }

  function handleBidCancelSigned(_txHash: string) {
    showSuccess('Bid cancelled');
    setSigningBidCancel(null);
    setCancellingBidId(null);
    void refresh();
  }

  return (
    <div className="flex w-full flex-col gap-4 rounded-md border border-solid border-neutral-border bg-default-background p-4">
      <div className="flex w-full items-center justify-between">
        <span className="text-heading-3 font-heading-3 text-default-font">{headline}</span>
        <span className="text-caption font-caption text-subtext-color">
          Min bid {formatXrp(auction.min_bid_drops)} XRP — Reserve {formatXrp(auction.reserve_price_drops)} XRP
        </span>
      </div>

      {isActive && !ended && <AuctionCountdown expiresAt={auction.expires_at} />}

      <div className="flex flex-col gap-1">
        {isActive ? (
          <span className="text-caption font-caption text-subtext-color">
            Auction ends {formatEndsAt(auction.expires_at)}
          </span>
        ) : auction.settled_at ? (
          <span className="text-caption font-caption text-subtext-color">
            Ended {formatEndsAt(auction.settled_at)}
          </span>
        ) : null}

        {/* Settled — show winning bid + winner */}
        {auction.status === 'settled' && auction.winning_bid_drops && (
          <span className="text-body font-body text-default-font">
            Sold for <strong>{formatXrp(auction.winning_bid_drops)} XRP</strong>
            {isWinner ? ' (you won this auction)' : auction.winning_bidder_address && (
              <> to {auction.winning_bidder_address.slice(0, 6)}…{auction.winning_bidder_address.slice(-4)}</>
            )}
          </span>
        )}

        {/* Expired without reserve — bidders need to know to cancel their bids */}
        {auction.status === 'expired_no_reserve' && (
          <span className="text-body font-body text-warning-700">
            No bid met the reserve of {formatXrp(auction.reserve_price_drops)} XRP. If you bid, your offer is still live on chain — cancel it from your wallet to release the funds.
          </span>
        )}

        {/* Stuck — settlement keeps failing */}
        {auction.status === 'settlement_stuck' && (
          <span className="text-body font-body text-warning-700">
            Settlement is taking longer than expected. We'll keep trying — check back shortly.
          </span>
        )}

        {/* Cancelled with live bids — bidders need to cancel their offers to
            release funds. We only render the auction view at all in this state
            when there are unmatched bids; otherwise the cancellation is silent. */}
        {auction.status === 'cancelled' && (
          <span className="text-body font-body text-warning-700">
            Auction was cancelled. If you bid, your offer is still live on chain — cancel it from your wallet to release the funds.
          </span>
        )}

        {/* Active state — current top bid */}
        {isActive && highestBid && (
          <span className="text-body font-body text-default-font">
            Current top bid: <strong>{formatXrp(highestBid.amount_drops)} XRP</strong>
          </span>
        )}
      </div>

      {isActive && (
        <div className="flex w-full items-center gap-2">
          {isOwner ? (
            <Button
              className="h-10 w-auto flex-none"
              variant="destructive-primary"
              size="large"
              onClick={handleCancel}
              disabled={ended}
            >
              Cancel auction
            </Button>
          ) : (
            <Button
              className="h-10 w-auto flex-none"
              size="large"
              onClick={() => setBidOpen(true)}
              disabled={ended || !user}
            >
              Make Offer
            </Button>
          )}
        </div>
      )}

      {bids.length > 0 && (
        <div className="flex w-full flex-col gap-2">
          <span className="text-caption-bold font-caption-bold text-default-font">Bid history</span>
          <div className="flex w-full flex-col gap-1">
            {bids.slice(0, 10).map((b) => {
              const isOwnBid = !!user && user.xrpl_address === b.owner_address;
              return (
                <div key={b.offer_id} className="flex w-full items-center justify-between gap-2 rounded border border-solid border-neutral-border px-3 py-1.5">
                  <Link
                    to={`/wallets/${b.owner_address}`}
                    className="text-caption font-caption text-brand-700 hover:text-brand-600 underline"
                  >
                    {formatAddress(b.owner_address)}
                  </Link>
                  <div className="flex items-center gap-2">
                    {isOwnBid && (
                      <Button
                        size="small"
                        variant="destructive-secondary"
                        onClick={() => void handleCancelOwnBid(b.offer_id)}
                        disabled={cancellingBidId === b.offer_id}
                      >
                        Cancel
                      </Button>
                    )}
                    <span className="text-caption-bold font-caption-bold text-default-font">
                      {formatXrp(b.amount_drops)} XRP
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <PlaceBidModal
        open={bidOpen}
        onClose={() => setBidOpen(false)}
        auctionId={auction.id}
        minBidDrops={auction.min_bid_drops}
        reservePriceDrops={auction.reserve_price_drops}
        highestBidDrops={highestBid?.amount_drops}
        onBidConfirmed={() => void refresh()}
      />

      {signingCancel && (
        <WalletSigningModal
          open={true}
          onClose={() => setSigningCancel(null)}
          title="Cancel auction"
          payload={signingCancel}
          onSuccess={handleCancelSigned}
          onRejected={() => {
            showError('Cancellation rejected');
            setSigningCancel(null);
          }}
          onExpired={() => {
            showError('Sign request expired');
            setSigningCancel(null);
          }}
        />
      )}

      {signingBidCancel && (
        <WalletSigningModal
          open={true}
          onClose={() => {
            setSigningBidCancel(null);
            setCancellingBidId(null);
          }}
          title="Cancel bid"
          payload={signingBidCancel}
          onSuccess={handleBidCancelSigned}
          onRejected={() => {
            showError('Cancellation rejected');
            setSigningBidCancel(null);
            setCancellingBidId(null);
          }}
          onExpired={() => {
            showError('Sign request expired');
            setSigningBidCancel(null);
            setCancellingBidId(null);
          }}
        />
      )}
    </div>
  );
};
